import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@seller-hermes/utils/cn';

const alertVariants = cva(
  'w-full rounded-lg border px-4 py-3 grid [&:has(>svg)]:grid-cols-[calc(theme(spacing.4)_1fr] grid-cols-[0_1fr] [&:has(>svg)]:gap-x-7 gap-y-1 items-start auto-rows-max [&>svg]:w-4 [&>svg]:h-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive selection:bg-destructive/20 selection:text-destructive',
        warning:
          'bg-[theme(colors.yellow.400/.1)] text-[theme(colors.yellow.500)] border-[theme(colors.yellow.300)] [&>svg]:text-[theme(colors.yellow.500)]  selection:bg-[theme(colors.yellow.500/.2)] selection:text-[theme(colors.yellow.900)] dark:selection:text-[theme(colors.yellow.200)]',
        info: 'bg-[theme(colors.blue.400/.1)] text-[theme(colors.blue.500)] border-[theme(colors.blue.300)] [&>svg]:text-[theme(colors.blue.500)] selection:bg-[theme(colors.blue.500/.2)] selection:text-[theme(colors.blue.900)] dark:selection:text-[theme(colors.blue.200)]',
        success:
          'bg-[theme(colors.green.400/.1)] text-[theme(colors.green.500)] border-[theme(colors.green.300)] [&>svg]:text-[theme(colors.green.500)] selection:bg-[theme(colors.green.500/.2)] selection:text-[theme(colors.green.900)] dark:selection:text-[theme(colors.green.200)]',
        error:
          'bg-[theme(colors.red.400/.1)] text-[theme(colors.red.500)] border-[theme(colors.red.300)] [&>svg]:text-[theme(colors.red.500)] selection:bg-[theme(colors.red.500/.2)] selection:text-[theme(colors.red.900)] dark:selection:text-[theme(colors.red.200)]',
        neutral:
          'bg-[theme(colors.gray.400/.1)] text-[theme(colors.gray.500)] border-[theme(colors.gray.300)] [&>svg]:text-[theme(colors.gray.500)] selection:bg-[theme(colors.gray.500/.2)] selection:text-[theme(colors.gray.900)] dark:selection:text-[theme(colors.gray.200)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h3
    ref={ref}
    className={cn(
      'col-start-2 line-clamp-1 text-sm font-bold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('col-start-2 text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export default Object.assign(Alert, {
  Title: AlertTitle,
  Description: AlertDescription,
});
